export type GasId = 'h2' | 'o2' | 'co2' | 'nh3' | 'cl2' | 'so2' | 'hcl' | 'no2' | 'n2' | 'h2s';

export type GasSolubility = 'insoluble' | 'slightly-soluble' | 'soluble' | 'very-soluble';

export interface GasProperties {
  id: GasId;
  name: string;
  formula: string;
  molarMass: number;
  densityRelativeToAir: number;
  solubility: GasSolubility;
  color: string;
  odor: string;
  toxicity: string;
  flammability: string;
  acidity?: string;
  testHints: string[];
}

export const gasDatabase: Record<GasId, GasProperties> = {
  h2: {
    id: 'h2',
    name: 'Hydrogen',
    formula: 'H₂',
    molarMass: 2.016,
    densityRelativeToAir: 0.07,
    solubility: 'insoluble',
    color: 'colourless',
    odor: 'odourless',
    toxicity: 'low acute toxicity but highly flammable',
    flammability: 'highly flammable',
    testHints: ['Burns with a squeaky pop', 'Collect over water', 'Never test in a sealed tube'],
  },
  o2: {
    id: 'o2',
    name: 'Oxygen',
    formula: 'O₂',
    molarMass: 31.998,
    densityRelativeToAir: 1.11,
    solubility: 'slightly-soluble',
    color: 'colourless',
    odor: 'odourless',
    toxicity: 'non-toxic in moderate quantities',
    flammability: 'supports combustion',
    testHints: ['Relights a glowing splint', 'Can be collected over water'],
  },
  co2: {
    id: 'co2',
    name: 'Carbon dioxide',
    formula: 'CO₂',
    molarMass: 44.01,
    densityRelativeToAir: 1.53,
    solubility: 'slightly-soluble',
    color: 'colourless',
    odor: 'odourless',
    toxicity: 'low toxicity but can displace oxygen',
    flammability: 'non-flammable',
    testHints: ['Turns limewater milky', 'Can be collected over water'],
  },
  nh3: {
    id: 'nh3',
    name: 'Ammonia',
    formula: 'NH₃',
    molarMass: 17.031,
    densityRelativeToAir: 0.59,
    solubility: 'very-soluble',
    color: 'colourless',
    odor: 'pungent, choking',
    toxicity: 'toxic and irritating',
    flammability: 'flammable in air at certain concentrations',
    acidity: 'alkaline/basic',
    testHints: ['Damp red litmus turns blue', 'Forms white fumes with HCl vapour'],
  },
  cl2: {
    id: 'cl2',
    name: 'Chlorine',
    formula: 'Cl₂',
    molarMass: 70.90,
    densityRelativeToAir: 2.44,
    solubility: 'soluble',
    color: 'pale yellow-green',
    odor: 'pungent, choking',
    toxicity: 'highly toxic and corrosive',
    flammability: 'not generally flammable',
    acidity: 'acidic',
    testHints: ['Bleaches damp litmus paper', 'Use in a fume cupboard'],
  },
  so2: {
    id: 'so2',
    name: 'Sulfur dioxide',
    formula: 'SO₂',
    molarMass: 64.06,
    densityRelativeToAir: 2.20,
    solubility: 'very-soluble',
    color: 'colourless',
    odor: 'pungent, choking',
    toxicity: 'toxic and irritating',
    flammability: 'not flammable',
    acidity: 'acidic',
    testHints: ['Turns acidified dichromate paper green', 'Acidic gas'],
  },
  hcl: {
    id: 'hcl',
    name: 'Hydrogen chloride',
    formula: 'HCl',
    molarMass: 36.46,
    densityRelativeToAir: 1.27,
    solubility: 'very-soluble',
    color: 'colourless',
    odor: 'pungent',
    toxicity: 'corrosive',
    flammability: 'non-flammable',
    acidity: 'acidic',
    testHints: ['Turns damp blue litmus red', 'Forms white fumes with ammonia'],
  },
  no2: {
    id: 'no2',
    name: 'Nitrogen dioxide',
    formula: 'NO₂',
    molarMass: 46.005,
    densityRelativeToAir: 1.59,
    solubility: 'soluble',
    color: 'brown',
    odor: 'sharp',
    toxicity: 'toxic and oxidising',
    flammability: 'not flammable',
    acidity: 'acidic',
    testHints: ['Brown gas', 'Turns damp blue litmus red'],
  },
  n2: {
    id: 'n2',
    name: 'Nitrogen',
    formula: 'N₂',
    molarMass: 28.014,
    densityRelativeToAir: 0.97,
    solubility: 'slightly-soluble',
    color: 'colourless',
    odor: 'odourless',
    toxicity: 'inert and non-toxic',
    flammability: 'non-flammable',
    testHints: ['Extinguishes a burning splint', 'No special positive test'],
  },
  h2s: {
    id: 'h2s',
    name: 'Hydrogen sulfide',
    formula: 'H₂S',
    molarMass: 34.08,
    densityRelativeToAir: 1.19,
    solubility: 'slightly-soluble',
    color: 'colourless',
    odor: 'rotten eggs',
    toxicity: 'highly toxic',
    flammability: 'flammable',
    acidity: 'acidic',
    testHints: ['Turns lead acetate paper black', 'Highly toxic, use in a fume cupboard'],
  },
};

export function getGas(gasId: string): GasProperties {
  const id = gasId.toLowerCase() as GasId;
  if (!(id in gasDatabase)) {
    throw new RangeError(`Unknown gas: ${gasId}`);
  }
  return gasDatabase[id];
}

export function listGasIds(): string[] {
  return Object.keys(gasDatabase) as GasId[];
}
